/**
 * <video-player> progressively enhances Shopify-hosted video, deferred HLS streams and Vimeo poster facades.
 * Autoplay media runs only while visible and when reduced motion is not requested. Vimeo iframes are injected at
 * idle for backgrounds or after facade activation for controlled video; no Vimeo SDK is loaded.
 */
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const IN_VIEW_THRESHOLD = 0.15;
const VIMEO_ORIGIN = 'https://player.vimeo.com';

class VideoPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.streamUrl = this.video?.dataset.streamUrl;
    // A stream playlist (data-stream-playlist: further .m3u8 URLs) plays in order and then loops as a whole
    this.streamList = this.streamUrl ? [this.streamUrl, ...(this.video.dataset.streamPlaylist || '').split(',').filter(Boolean)] : [];
    this.streamIndex = 0;
    this.vimeoTarget = this.querySelector('[data-vimeo-target]');
    this.facade = this.querySelector('[data-vimeo-load]');
    this.toggle = this.querySelector('[data-video-toggle]');
    this.autoplay = this.hasAttribute('data-autoplay');
    this.userPaused = false;
    this.inView = false;

    if (this.facade) {
      this.facade.removeAttribute('target');
      this.facade.removeAttribute('rel');
      this.facade.querySelector('[data-vimeo-new-tab]')?.remove();
    }

    const background = this.closest('.home-hero__media, .page-hero__media, .section__media');
    const controlHost = background?.closest('.home-hero, .page-hero, .section__panel');
    if (this.toggle && controlHost) controlHost.append(this.toggle);

    this.onFacadeClick = (event) => {
      const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (event.defaultPrevented || event.button !== 0 || modified) return;
      event.preventDefault();
      this.injectVimeo(false);
    };
    this.onToggleClick = () => {
      this.userPaused = !this.userPaused;
      this.toggle.toggleAttribute('data-paused', this.userPaused);
      if (this.userPaused) this.pause();
      else this.sync();
    };
    this.onStreamIntent = (event) => {
      if (event.type === 'keydown' && event.key !== ' ' && event.key !== 'Enter') return;
      if (!this.streamUrl || this.autoplay || this.streamReady) return;
      if (this.canPlayNativeHls()) this.ensureStream();
      else this.playStream();
    };
    this.onVideoPlay = () => {
      if (!this.streamUrl || this.streamReady) return;
      this.video.pause();
      this.playStream();
    };
    this.facade?.addEventListener('click', this.onFacadeClick);
    this.toggle?.addEventListener('click', this.onToggleClick);
    this.video?.addEventListener('pointerdown', this.onStreamIntent);
    this.video?.addEventListener('keydown', this.onStreamIntent);
    this.video?.addEventListener('play', this.onVideoPlay);
    this.onStreamEnded = () => this.nextStream();
    if (this.streamList.length > 1) this.video.addEventListener('ended', this.onStreamEnded);

    // Controls mode: attach the stream when the player nears the viewport, so the native play button works; with
    // preload none (native) and autoStartLoad off (hls.js) no media is fetched until the visitor presses play.
    if (this.streamUrl && !this.autoplay && this.video) {
      this.onStreamPlay = () => this.hls?.startLoad();
      this.video.addEventListener('play', this.onStreamPlay);
      this.streamObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.streamObserver.disconnect();
        this.ensureStream();
      }, { rootMargin: '200px' });
      this.streamObserver.observe(this);
    }

    if ((!this.video && !this.vimeoTarget) || !this.autoplay) return;

    this.motion = window.matchMedia(REDUCED_MOTION);
    this.onMotionChange = () => this.sync();
    this.motion.addEventListener('change', this.onMotionChange);
    if (this.video) this.video.muted = true;

    this.observer = new IntersectionObserver(
      (entries) => {
        this.inView = entries[entries.length - 1].isIntersecting;
        this.sync();
      },
      { threshold: IN_VIEW_THRESHOLD }
    );
    this.observer.observe(this);

    this.sizer = new ResizeObserver(() => this.sync());
    this.sizer.observe(this.video ?? this.vimeoTarget);
  }

  disconnectedCallback() {
    // Invalidates any stream load still awaiting the hls.js import
    this.streamGeneration = (this.streamGeneration ?? 0) + 1;
    this.observer?.disconnect();
    this.streamObserver?.disconnect();
    this.video?.removeEventListener('play', this.onStreamPlay);
    this.sizer?.disconnect();
    this.motion?.removeEventListener('change', this.onMotionChange);
    this.facade?.removeEventListener('click', this.onFacadeClick);
    this.toggle?.removeEventListener('click', this.onToggleClick);
    this.video?.removeEventListener('pointerdown', this.onStreamIntent);
    this.video?.removeEventListener('keydown', this.onStreamIntent);
    this.video?.removeEventListener('play', this.onVideoPlay);
    this.video?.removeEventListener('ended', this.onStreamEnded);
    this.hls?.destroy();
    if (this.nativeStream && this.video) {
      this.video.removeAttribute('src');
      this.video.load();
    }
    this.hls = null;
    this.nativeStream = false;
    this.streamReady = false;
    this.streamPromise = null;
    this.cancelIdle();
  }

  sync() {
    const media = this.video ?? this.vimeoTarget;
    const shouldPlay = this.inView && !this.motion.matches && !this.userPaused && media.getClientRects().length > 0;
    if (shouldPlay) this.play();
    else this.pause();
  }

  play() {
    this.playRequested = true;
    if (this.vimeoTarget) {
      if (this.vimeoFrame) {
        this.postVimeo('play');
        this.setAttribute('data-playing', '');
      } else if (this.autoplay) {
        this.scheduleVimeo();
      } else {
        this.injectVimeo(true);
      }
      return;
    }

    if (this.streamUrl) {
      this.playStream();
      return;
    }

    const attempt = this.video?.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(() => this.setAttribute('data-playing', '')).catch(() => {});
    } else if (this.video) {
      this.setAttribute('data-playing', '');
    }
  }

  async playStream() {
    this.playRequested = true;
    if (!(await this.ensureStream())) return;
    if (!this.playRequested) return;
    if (this.autoplay && (!this.inView || this.motion?.matches || this.userPaused)) return;

    const attempt = this.video.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(() => this.setAttribute('data-playing', '')).catch(() => {});
    } else {
      this.setAttribute('data-playing', '');
    }
  }

  ensureStream() {
    if (this.streamReady) return Promise.resolve(true);
    if (this.streamPromise) return this.streamPromise;

    const generation = this.streamGeneration ?? 0;
    this.streamPromise = this.loadStream(generation).then((ready) => {
      if (generation !== (this.streamGeneration ?? 0)) return false;
      this.streamReady = ready;
      return ready;
    });
    return this.streamPromise;
  }

  async loadStream(generation) {
    if (!this.video || !this.streamUrl) return false;

    // Background loops prefer hls.js: native HLS (Safari, and Chrome since it plays HLS itself) restarts every loop at
    // the first, lowest rung of the ladder, so a short loop goes blurry, sharp, blurry. hls.js keeps its bandwidth
    // estimate across loops. Controls-mode video stays native where available (most reliable tap-to-play on iOS).
    const native = this.canPlayNativeHls();
    if (native && !this.autoplay) return this.attachNative();

    try {
      const { default: Hls } = await import('hls');
      if (!this.isConnected || generation !== (this.streamGeneration ?? 0)) return false;
      if (!Hls.isSupported()) return native ? this.attachNative() : false;
      this.hls?.destroy();
      // The default bandwidth guess (500 kbps) starts short loops at the lowest rung and they end before ABR climbs;
      // assume 5 Mbps for the first fragment, then measure. capLevelToPlayerSize still stops it above the element's size.
      this.hls = new Hls({
        capLevelToPlayerSize: true,
        abrEwmaDefaultEstimate: 5_000_000,
        maxBufferLength: 10,
        autoStartLoad: this.autoplay
      });
      this.hls.attachMedia(this.video);
      this.hls.loadSource(this.streamUrl);
      return true;
    } catch {
      if (!this.isConnected || generation !== (this.streamGeneration ?? 0)) return false;
      return this.canPlayNativeHls() ? this.attachNative() : false;
    }
  }

  /** Playlist: switch to the next stream (wrapping round) and keep playing if playback is still wanted. */
  nextStream() {
    if (!this.streamReady || this.streamList.length < 2) return;
    this.streamIndex = (this.streamIndex + 1) % this.streamList.length;
    this.streamUrl = this.streamList[this.streamIndex];
    if (this.hls) this.hls.loadSource(this.streamUrl);
    else if (this.nativeStream) this.video.src = this.streamUrl;
    if (!this.autoplay || (this.inView && !this.motion?.matches && !this.userPaused)) {
      this.video.play()?.catch?.(() => {});
    }
  }

  attachNative() {
    this.nativeStream = true;
    this.video.src = this.streamUrl;
    return true;
  }

  canPlayNativeHls() {
    return Boolean(this.video?.canPlayType('application/vnd.apple.mpegurl'));
  }

  pause() {
    this.playRequested = false;
    this.cancelIdle();
    this.video?.pause();
    this.postVimeo('pause');
    this.removeAttribute('data-playing');
  }

  scheduleVimeo() {
    if (this.idleHandle != null || this.vimeoFrame) return;
    const run = () => {
      this.idleHandle = null;
      const mediaVisible = this.vimeoTarget.getClientRects().length > 0;
      if (this.inView && !this.motion.matches && !this.userPaused && mediaVisible) this.injectVimeo(true);
    };
    if ('requestIdleCallback' in window) this.idleHandle = window.requestIdleCallback(run);
    else this.idleHandle = window.setTimeout(run);
  }

  cancelIdle() {
    if (this.idleHandle == null) return;
    if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.idleHandle);
    else window.clearTimeout(this.idleHandle);
    this.idleHandle = null;
  }

  injectVimeo(background) {
    if (this.vimeoFrame) {
      this.postVimeo('play');
      this.setAttribute('data-playing', '');
      return;
    }

    const id = this.dataset.vimeoId;
    if (!/^\d+$/.test(id ?? '')) return;

    const params = new URLSearchParams();
    if (this.dataset.vimeoHash) params.set('h', this.dataset.vimeoHash);
    if (background) {
      params.set('background', '1');
      params.set('muted', '1');
      params.set('loop', '1');
    } else {
      params.set('controls', '1');
    }
    params.set('autoplay', '1');
    params.set('dnt', '1');
    params.set('playsinline', '1');

    const frame = document.createElement('iframe');
    frame.className = 'video__iframe';
    frame.src = `${VIMEO_ORIGIN}/video/${id}?${params}`;
    frame.title = this.dataset.vimeoTitle ?? '';
    frame.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
    frame.allowFullscreen = true;
    frame.addEventListener('load', () => {
      const mayAutoplay = background && this.playRequested && (!this.autoplay || (this.inView && !this.motion?.matches && !this.userPaused));
      if (!background || mayAutoplay) {
        this.postVimeo('play');
        this.setAttribute('data-playing', '');
        if (this.facade) this.facade.hidden = true;
      } else {
        this.postVimeo('pause');
      }
    });
    this.vimeoFrame = frame;
    this.vimeoTarget.append(frame);
  }

  postVimeo(method) {
    this.vimeoFrame?.contentWindow?.postMessage(JSON.stringify({ method }), VIMEO_ORIGIN);
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer);
}
